"use client";

/**
 * ShopMock — animated mock of the askFinz Shop marketplace for /apps/shop.
 *
 * Reflects the live surface: an AI prompt bar at the top ("Describe what
 * you need"), a multi-vendor discovery grid below, and a side cart that
 * fills as the AI assembles a bundle. The checkout pill flips to "Paid" and
 * the order pipeline lights up.
 *
 * Trademark guardrail: no real-world brand names. Generic SKUs only.
 * Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  ShoppingBag,
  Package,
  Truck,
  CheckCircle2,
  CreditCard,
  Sparkles,
  ArrowUpRight,
  Store,
} from "lucide-react";

type Product = {
  id: string;
  name: string;
  vendor: string;
  price: number;
  sku: string;
};

const PRODUCTS: Product[] = [
  { id: "p1", name: "Linen desk mat",     vendor: "Hinoki Studio",   price: 54,  sku: "HNK-DM-03" },
  { id: "p2", name: "Monitor riser",      vendor: "Atelier Goods",   price: 89,  sku: "ATL-MR-07" },
  { id: "p3", name: "Cable management",   vendor: "Hinoki Studio",   price: 28,  sku: "HNK-CM-11" },
  { id: "p4", name: "Walnut pen holder",  vendor: "Atelier Goods",   price: 42,  sku: "ATL-PH-02" },
  { id: "p5", name: "Warm task light",    vendor: "Nordic Supply",   price: 116, sku: "NRD-TL-05" },
  { id: "p6", name: "Felt cable tray",    vendor: "Nordic Supply",   price: 36,  sku: "NRD-CT-09" },
];

type PipelineStep = {
  id: "placed" | "paid" | "packed" | "shipped";
  label: string;
  icon: typeof ShoppingBag;
};

const PIPELINE: PipelineStep[] = [
  { id: "placed",  label: "Placed",  icon: ShoppingBag },
  { id: "paid",    label: "Paid",    icon: CreditCard },
  { id: "packed",  label: "Packed",  icon: Package },
  { id: "shipped", label: "Shipped", icon: Truck },
];

const AI_PROMPT = "Calm warm desk corner under €700";

// Bundle the AI assembles — product ids added in sequence
const BUNDLE_ORDER = ["p1", "p5", "p2", "p3"];

export function ShopMock() {
  const [typedChars, setTypedChars] = useState(AI_PROMPT.length); // pre-typed on first paint
  const [cart, setCart] = useState<Product[]>([]);
  const [pulseId, setPulseId] = useState<string | null>(null);
  const [pipelineIdx, setPipelineIdx] = useState(0);
  const [paid, setPaid] = useState(false);
  const [aiStatus, setAiStatus] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setCart([]);
      setPaid(false);
      setPipelineIdx(0);
      setAiStatus("Sourcing across vendors…");
      setPulseId(null);
      setTypedChars(AI_PROMPT.length);

      // AI assembles the bundle item by item
      BUNDLE_ORDER.forEach((id, i) => {
        const delay = 700 + i * 900;
        schedule(() => {
          setPulseId(id);
          const product = PRODUCTS.find((p) => p.id === id)!;
          setCart((c) => [...c, product]);
          setAiStatus(`Added ${product.name}`);
        }, delay);
        schedule(() => setPulseId(null), delay + 500);
      });

      const bundleDone = 700 + BUNDLE_ORDER.length * 900;

      // Status clears
      schedule(() => setAiStatus("Cart ready · 4 items"), bundleDone + 100);

      // Checkout flips to paid
      schedule(() => {
        setPaid(true);
        setAiStatus(null);
      }, bundleDone + 1100);

      // Pipeline lights up
      PIPELINE.forEach((_, i) => {
        schedule(() => setPipelineIdx(i + 1), bundleDone + 1500 + i * 650);
      });

      // Loop
      schedule(runCycle, bundleDone + 1500 + PIPELINE.length * 650 + 2500);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const total = cart.reduce((s, p) => s + p.price, 0);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            shop.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Live
          </span>
        </div>

        {/* AI prompt bar */}
        <div className="px-4 py-3 border-b hairline bg-foreground/[0.02]">
          <div className="flex items-center gap-2.5 rounded-xl border hairline-strong bg-background px-3.5 py-2.5">
            <Sparkles className="h-4 w-4 text-foreground/65 shrink-0" />
            <div className="flex-1 text-[12px] font-mono text-foreground/80 truncate">
              {AI_PROMPT.slice(0, typedChars)}
              <span className="inline-block h-3 w-1 ml-0.5 bg-foreground/60 align-middle animate-pulse" />
            </div>
            {aiStatus && (
              <span className="ml-2 text-[10px] tracking-[0.14em] uppercase text-foreground/65 font-mono whitespace-nowrap animate-[blur-fade-in_0.3s_ease-out]">
                {aiStatus}
              </span>
            )}
          </div>
        </div>

        {/* Storefront body — grid + cart */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-px bg-foreground/10">
          {/* Product grid */}
          <div className="bg-background p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] tracking-[0.16em] uppercase text-foreground/65 font-mono flex items-center gap-1.5">
                <Store className="h-3 w-3" />
                3 vendors · 6 items
              </div>
              <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                <Sparkles className="h-3 w-3" />
                AI bundle
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {PRODUCTS.map((p) => (
                <ProductTile key={p.id} p={p} pulse={pulseId === p.id} inCart={cart.some((c) => c.id === p.id)} />
              ))}
            </div>
          </div>

          {/* Cart rail */}
          <div className="bg-background p-4 min-h-[260px] flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag className="h-3.5 w-3.5 text-foreground/65" />
              <span className="text-[11px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                Cart · {cart.length}
              </span>
            </div>

            <div className="flex-1 space-y-1.5 overflow-hidden">
              {cart.map((c, i) => (
                <div
                  key={`${c.id}-${i}`}
                  className="flex items-center justify-between rounded-md border hairline bg-foreground/[0.02] px-2.5 py-1.5 text-[11px] animate-[blur-fade-in_0.4s_ease-out]"
                >
                  <div className="min-w-0">
                    <div className="truncate text-foreground/85">{c.name}</div>
                    <div className="text-[9px] text-foreground/50 font-mono">{c.vendor}</div>
                  </div>
                  <div className="ml-2 font-mono text-foreground/70 shrink-0">€{c.price}</div>
                </div>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t hairline">
              <div className="flex items-center justify-between text-[11px] mb-2">
                <span className="text-foreground/65 tracking-[0.16em] uppercase font-mono">Total</span>
                <span className="font-mono text-foreground/85 tabular-num">€{total}</span>
              </div>
              <button
                type="button"
                className={
                  "w-full inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium transition-all " +
                  (paid
                    ? "bg-[hsl(var(--accent))] text-background"
                    : cart.length > 0
                    ? "bg-foreground text-background hover:opacity-90"
                    : "bg-foreground/20 text-foreground/50 cursor-not-allowed")
                }
              >
                {paid ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Paid · order #8241
                  </>
                ) : (
                  <>
                    <CreditCard className="h-3.5 w-3.5" />
                    Checkout
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Order pipeline */}
        <div className="border-t hairline bg-foreground/[0.02] px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-foreground/65" />
            <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              Order pipeline · wired to Mail + Workflow
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {PIPELINE.map((step, i) => {
              const Icon = step.icon;
              const done = i < pipelineIdx;
              const active = i === pipelineIdx - 1;
              return (
                <div key={step.id} className="flex items-center gap-1.5 flex-1">
                  <div
                    className={
                      "flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[10px] flex-1 transition-all " +
                      (done
                        ? "bg-foreground text-background"
                        : "bg-background border hairline text-foreground/65")
                    }
                  >
                    <Icon className={"h-3 w-3 " + (active ? "animate-pulse" : "")} />
                    <span className="truncate font-mono tracking-[0.12em] uppercase text-[9px]">
                      {step.label}
                    </span>
                    {done && <CheckCircle2 className="ml-auto h-3 w-3 shrink-0" />}
                  </div>
                  {i < PIPELINE.length - 1 && (
                    <div
                      className={
                        "h-px w-3 shrink-0 transition-all " +
                        (i < pipelineIdx - 1 ? "bg-foreground" : "bg-foreground/15")
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Live at <span className="font-mono text-foreground/65">shop.askfinz.ai</span></span>
      </div>
    </div>
  );
}

function ProductTile({ p, pulse, inCart }: { p: Product; pulse: boolean; inCart: boolean }) {
  return (
    <div
      className={
        "relative rounded-xl border hairline bg-foreground/[0.02] p-3 min-h-[100px] flex flex-col justify-between transition-all " +
        (pulse ? "ring-2 ring-[hsl(var(--accent))]/50 scale-[1.02]" : "") +
        (inCart ? " border-foreground/30 bg-foreground/[0.04]" : "")
      }
    >
      {inCart && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 rounded-full bg-foreground text-background px-1.5 py-0.5 text-[8px] tracking-[0.14em] uppercase font-mono animate-[blur-fade-in_0.4s_ease-out]">
          <CheckCircle2 className="h-2 w-2" />
          In cart
        </span>
      )}
      <div>
        <div className="h-8 w-8 rounded-lg bg-foreground/10 mb-2 flex items-center justify-center">
          <ShoppingBag className="h-4 w-4 text-foreground/55" />
        </div>
        <div className="text-[12px] font-medium tracking-tight text-foreground/90 leading-snug">{p.name}</div>
        <div className="text-[9px] font-mono text-foreground/50 mt-0.5">{p.vendor}</div>
      </div>
      <div className="mt-2 font-mono text-[12px] text-foreground/80">€{p.price}</div>
    </div>
  );
}
